<script setup lang="ts">
import { GpDropdown } from '@gportal/uikit';
import type { Flavour } from 'gpcore-grpc/gpcore/api/cloud/v1/flavour_pb';
import {
  type BasicImage,
  type Image,
  ImageRelease,
  type OperatingSystem,
} from 'gpcore-grpc/gpcore/api/cloud/v1/image_pb';
import { CloudProviderType } from 'gpcore-grpc/gpcore/type/v1/cloud_provider_pb';
import { computed, onMounted, watchEffect } from 'vue';
import SectionLoadingMessage from '../components/SectionLoadingMessage.vue';
import SectionMessage from '../components/SectionMessage.vue';
import usePublicImages from './composables/usePublicImages';
import ImageOsPickerItem from './ImageOsItem.vue';
import SectionErrorMessage from '../components/SectionErrorMessage.vue';

export interface Props {
  installedImage?: Image;
  cloudProviderType?: CloudProviderType | null;
  flavour?: Flavour;
  forcePreselect?: boolean;
}

const {
  installedImage = null,
  cloudProviderType = null,
  flavour = null,
  forcePreselect = false,
} = defineProps<Props>();

const image = defineModel<BasicImage>('modelValue');
const isValid = defineModel<boolean>('isValid');

const {
  data: publicImages,
  isLoading,
  fetch,
  selectedOs,
  selectedOsImageId,
  findPublicImage,
  error,
} = usePublicImages();

watchEffect(() => {
  isValid.value = selectedOs.value != null && image.value != null;
});

const osImages = computed(() => {
  if (selectedOs.value != null) {
    return selectedOs.value.images.map((image) => ({ key: image.id, label: image.name }));
  }

  return [];
});

const handleOsSelect = (os: OperatingSystem) => {
  selectedOs.value = os;
  // preselect the first image
  selectedOsImageId.value = os.images[0].id;
  image.value = selectedOs.value.images.find((image) => image.id === selectedOsImageId.value);
};

const handleOsImageSelect = (imageId) => {
  selectedOsImageId.value = imageId;
  image.value = selectedOs.value.images.find((image) => image.id === selectedOsImageId.value);
};

const preselectPublicImage = () => {
  if (publicImages.value.length === 0) {
    image.value = null;

    return;
  }

  if (image.value != null) {
    const img = findPublicImage(image.value);
    if (img != null) {
      image.value = img;
      return;
    }
  }

  // if we have a preselected image, try to find it in the list
  if (installedImage != null) {
    const img = findPublicImage(installedImage);
    if (img != null) {
      image.value = img;
      return;
    }
  }

  // otherwise, select the first image
  if (publicImages.value[0].images.length > 0) {
    selectedOs.value = publicImages.value[0];
    selectedOsImageId.value = publicImages.value[0].images[0].id;
    image.value = publicImages.value[0].images[0];
  }

  return;
};

onMounted(async () => {
  if (publicImages.value.length === 0) {
    await fetch(cloudProviderType);
  }

  if (forcePreselect) {
    preselectPublicImage();
  } else {
    selectedOs.value = null;
    selectedOsImageId.value = null;
    image.value = null;
  }
});
</script>

<template>
  <SectionLoadingMessage v-if="isLoading">Loading public images ...</SectionLoadingMessage>

  <SectionErrorMessage
    v-else-if="error"
    :error="error"
    v-playwright="'nodes-create-image-public-error'"
  >
    Error while loading public images
  </SectionErrorMessage>

  <SectionMessage v-else-if="publicImages.length === 0">
    No public images available
  </SectionMessage>

  <div v-else class="tw-mt-4">
    <div class="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2 lg:tw-grid-cols-4">
      <ImageOsPickerItem
        v-for="(os, index) in publicImages"
        :key="os.id"
        :os="os"
        :is-selected="selectedOs != null && os.id === selectedOs.id"
        :has-spla-license="os.images.length >= 0 ? os.images[0].containsSplaLicense : false"
        :flavour="flavour"
        @os-selected="handleOsSelect(os)"
        v-playwright="`nodes-create-image-public-item-${index}`"
      />
    </div>
    <GpDropdown
      v-if="selectedOs != null"
      class="tw-mt-4 tw-w-full md:tw-w-1/2 lg:tw-w-1/4"
      :model-value="selectedOsImageId"
      :data="osImages"
      :input-id="`${selectedOs.id}__images`"
      label="Select a version"
      @update:model-value="handleOsImageSelect"
    >
      <template #hint>
        <template v-if="image != null">
          <span v-if="image.release === ImageRelease.PRE_RELEASE" class="tw-text-gp-orange">
            Pre-Release
          </span>
          <span v-if="image!.release === ImageRelease.END_OF_LIFE" class="tw-text-gp-crab">
            End of Life
          </span>
        </template>
      </template>
      <template #itemContent="{ item }">
        <span>
          {{ item.label }}
          <span
            v-if="installedImage != null && item.key === installedImage.id"
            class="tw-text-gp-green"
          >
            (Installed)
          </span>
        </span>
      </template>
    </GpDropdown>
  </div>
</template>

<style scoped></style>
